/**
 * Extension host bootstrap (editor-core).
 *
 * The single React component that makes extensions *actually run* inside the
 * app. It:
 *
 *   1. Assembles concrete `HostServices` (runtime bridge, editor host API,
 *      settings, logging) plus locale-aware i18n wired to the workbench locale.
 *   2. Activates every discovered extension (from the UI registry, populated by
 *      `loadBundledExtensions` / gateway / local loaders) and re-activates ones
 *      registered later.
 *   3. Seeds per-extension settings defaults from each manifest `settingsSchema`.
 *   4. Mounts `<ExtensionShellProvider>` so resident contributions (sidebar
 *      panels, status-bar items, settings panels, commands) render and the
 *      command bridge is live.
 *
 * App layers mount `<ExtensionHostBootstrap>` near the root of their shell.
 */
import {
    createElement,
    useEffect,
    useMemo,
    useRef,
    useSyncExternalStore,
    type ReactNode,
} from "react";

import { createI18n, type LocaleParams } from "@magic-md/i18n";
import { useI18n } from "@magic-md/i18n/react";
import type { SettingsSnapshot } from "@magic-md/engine-client/config";
import type { ExtensionI18n, ExtensionManifest, JSONSchema } from "@magic-md/extension-sdk";

import { ExtensionShellProvider } from "./extension-shell-context";
import {
    activateExtension,
    deactivateExtension,
    getActiveExtensionExports,
    isExtensionActive,
} from "./extension-host";
import { extensionUIRegistry } from "./extension-ui-registry";
import { useExtensionHostStore } from "./extension-host-store";
import {
    createEditorHostApi,
    createHostSettingsServices,
    createRuntimeBridge,
    hostLogger,
} from "./create-host-services";
import {
    buildExtensionSettingsModel,
    buildExtensionSettingsSnapshot,
    getEffectiveExtensionEnabled,
} from "./extension-settings-model";
import type { HostI18nServices, HostServices } from "./host-services";
import { getPlatformService } from "../platform";
import { PLATFORM_CONNECTION_CHANGED_EVENT } from "../platform/engine-proxy";
import { getNotificationBus } from "../store/notification-bus";

/** Extract `properties[*].default` values from a settings JSON schema. */
function defaultsFromSchema(schema: JSONSchema | undefined): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    if (!schema?.properties) return out;
    for (const [key, prop] of Object.entries(schema.properties)) {
        if (prop && typeof prop === "object" && "default" in prop) {
            out[key] = (prop as JSONSchema).default;
        }
    }
    return out;
}

export interface ExtensionHostBootstrapProps {
    children: ReactNode;
    /** Activate discovered extensions on mount (default true). */
    autoActivate?: boolean;
}

export function ExtensionHostBootstrap({
    children,
    autoActivate = true,
}: ExtensionHostBootstrapProps): ReactNode {
    const active = useI18n();

    // Latest locale, read lazily by the i18n services (stable identity).
    const localeRef = useRef(active.locale);
    localeRef.current = active.locale;
    const fallbackRef = useRef(active.fallbackLocale);
    fallbackRef.current = active.fallbackLocale;

    const localeListeners = useRef(new Set<(locale: string) => void>());
    // extensionId → its declared i18n bundles/namespace (for translate()).
    const i18nConfigs = useRef(new Map<string, ExtensionI18n>());

    const i18n = useMemo<HostI18nServices>(
        () => ({
            get locale() {
                return localeRef.current;
            },
            translate(namespace: string, key: string, params?: LocaleParams) {
                const cfg = i18nConfigs.current.get(namespace);
                if (!cfg) return key;
                const instance = createI18n({
                    requestedLocale: localeRef.current,
                    fallbackLocale: fallbackRef.current,
                    bundles: cfg.bundles,
                });
                return instance.t(cfg.namespace, key, params);
            },
            onLocaleChanged(cb: (locale: string) => void) {
                localeListeners.current.add(cb);
                return () => {
                    localeListeners.current.delete(cb);
                };
            },
        }),
        [],
    );

    const services = useMemo<HostServices>(
        () => ({
            runtime: createRuntimeBridge(),
            host: createEditorHostApi(),
            i18n,
            settings: createHostSettingsServices(),
            log: hostLogger,
            getExtensionExports: getActiveExtensionExports,
        }),
        [i18n],
    );

    // Fan locale changes out to extension subscribers.
    useEffect(() => {
        for (const cb of localeListeners.current) cb(active.locale);
    }, [active.locale]);

    const enabledOverrides = useExtensionHostStore((state) => state.enabled);
    const effectiveEnabled = useExtensionHostStore((state) => state.effectiveEnabled);
    const extensionRegistryRevision = useSyncExternalStore(
        (onChange) => extensionUIRegistry.subscribe(onChange),
        () => extensionUIRegistry.revision(),
        () => extensionUIRegistry.revision(),
    );
    const extensionSettingsEntries = useMemo(
        () => buildExtensionSettingsModel().entries,
        [active.fallbackLocale, active.locale, extensionRegistryRevision],
    );

    useEffect(() => {
        let disposed = false;

        const syncEffectiveEnabled = async () => {
            if (extensionSettingsEntries.length === 0) {
                useExtensionHostStore.getState().setEffectiveEnabled({});
                return;
            }

            let serviceSnapshot: SettingsSnapshot | null = null;
            try {
                serviceSnapshot = await getPlatformService().settings.getSnapshot(
                    { owner: "extension" },
                    extensionSettingsEntries,
                );
            } catch {
                serviceSnapshot = null;
            }
            if (disposed) return;

            const store = useExtensionHostStore.getState();
            const mergedSnapshot = buildExtensionSettingsSnapshot({
                entries: extensionSettingsEntries,
                serviceSnapshot,
                settings: store.settings,
                defaults: store.defaults,
                enabled: store.enabled,
            });
            store.setEffectiveEnabled(
                getEffectiveExtensionEnabled(mergedSnapshot, extensionSettingsEntries),
            );
        };

        void syncEffectiveEnabled();
        const unsubscribeConfig = getNotificationBus().subscribe<{ changedKeys?: string[] }>(
            "event/config.changed",
            (params) => {
                if ((params.changedKeys ?? []).some((key) => key.startsWith("extensions."))) {
                    void syncEffectiveEnabled();
                }
            },
        );
        const handleConnectionChanged = () => {
            void syncEffectiveEnabled();
        };
        if (typeof window !== "undefined") {
            window.addEventListener(
                PLATFORM_CONNECTION_CHANGED_EVENT,
                handleConnectionChanged,
            );
        }

        return () => {
            disposed = true;
            unsubscribeConfig();
            if (typeof window !== "undefined") {
                window.removeEventListener(
                    PLATFORM_CONNECTION_CHANGED_EVENT,
                    handleConnectionChanged,
                );
            }
        };
    }, [extensionSettingsEntries]);

    // Activate/deactivate discovered extensions (now + whenever registry or
    // enablement changes).
    useEffect(() => {
        if (!autoActivate) return;
        let disposed = false;

        const reconcileAll = () => {
            const store = useExtensionHostStore.getState();
            for (const id of extensionUIRegistry.ids()) {
                const ext = extensionUIRegistry.getRaw(id);
                if (!ext) continue;
                if (ext.i18n) {
                    i18nConfigs.current.set(id, ext.i18n);
                    i18nConfigs.current.set(ext.i18n.namespace, ext.i18n);
                }
                const manifest = ext.manifest as ExtensionManifest;
                useExtensionHostStore
                    .getState()
                    .registerDefaults(id, defaultsFromSchema(manifest.settingsSchema));

                if (!store.isEnabled(id)) {
                    if (isExtensionActive(id)) {
                        void deactivateExtension(id, services).catch((err) => {
                            hostLogger(id, "error", `deactivation failed: ${String(err)}`);
                        });
                    }
                    continue;
                }

                if (isExtensionActive(id)) continue;
                void activateExtension(id, services).catch((err) => {
                    hostLogger(id, "error", `activation failed: ${String(err)}`);
                });
            }
        };

        reconcileAll();
        const unsubscribe = extensionUIRegistry.subscribe(() => {
            if (!disposed) reconcileAll();
        });

        return () => {
            disposed = true;
            unsubscribe();
        };
    }, [services, autoActivate, enabledOverrides, effectiveEnabled]);

    return createElement(ExtensionShellProvider, { services, children });
}
